import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "./Reveal";
import { FrenchFlag } from "./FrenchFlag";
import { Plus } from "lucide-react";
import { useLang } from "../i18n";
import { formatPrice } from "../currency";

const SZ = { en: "Size", fr: "Format", ar: "الحجم", bn: "আকার" };

const NotesPyramid = ({ notes, labels, testId }) => (
    <div className="space-y-0 border-t border-b border-gold/25 divide-y divide-gold/15" data-testid={testId}>
        {[
            [labels.top, notes.top],
            [labels.heart, notes.heart],
            [labels.base, notes.base],
        ].map(([label, items]) => (
            <div key={label} className="grid grid-cols-[110px_1fr] sm:grid-cols-[150px_1fr] gap-4 py-5">
                <span className="text-[10px] tracking-[0.25em] uppercase text-golddark pt-1">{label}</span>
                <span className="font-serif text-lg sm:text-xl text-charcoal italic">{items.join(" · ")}</span>
            </div>
        ))}
    </div>
);

export const ProductShowcase = ({ product, index, reversed = false, tint = "bg-ivory", onAdd }) => {
    const { t, lang } = useLang();
    const imgRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
    const [sizeIdx, setSizeIdx] = useState(0);
    const gallery = product.gallery?.length ? product.gallery : [product.image];
    const [activeImg, setActiveImg] = useState(gallery[0]);
    const shownImg = gallery.includes(activeImg) ? activeImg : gallery[0];

    const description = t(`products.${product.id}.desc`) ?? product.description;
    const translatedNotes = t(`products.${product.id}.notes`);
    const notes = translatedNotes?.top ? translatedNotes : product.notes;
    const labels = { top: t("notes.top"), heart: t("notes.heart"), base: t("notes.base") };
    const badge = product.badge === "Smoke Ritual" ? t("badge.smokeRitual") : t("badge.alcoholFree");
    const isFrance = !product.caption;
    const caption =
        product.caption === "Inspired by Arab Heritage"
            ? t("caption.heritage")
            : product.caption === "For the Majlis"
              ? t("caption.majlis")
              : t("caption.france");

    const size = product.sizes?.[sizeIdx];
    const priceInr = size ? size.inr : product.effectivePrice ?? product.price;
    const original = size ? size.original : product.originalPrice;
    const priceAed = size ? size.aed : product.aed ?? null;
    const sizeLabel = SZ[lang] ?? SZ.en;
    const inStock = size ? size.stock > 0 : product.in_stock !== false;
    const soldOut = { en: "Sold out", fr: "Épuisé", ar: "نفد المخزون", bn: "স্টক শেষ" }[lang] ?? "Sold out";

    const handleAdd = () => {
        if (!inStock) return;
        onAdd({
            ...product,
            id: size ? size.id : product.id,
            name: size ? `${product.name} · ${size.label}` : product.name,
            price: priceInr,
            aed: priceAed,
        });
    };

    return (
        <section id={product.id} className={`${tint} py-28 sm:py-40 px-6 sm:px-10`} data-testid={`product-section-${product.id}`}>
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
                <div className={reversed ? "lg:order-2" : ""}>
                    <div className="lg:sticky lg:top-28">
                        <Reveal>
                            <div ref={imgRef} className="relative overflow-hidden border border-gold/30 bg-charcoal aspect-[4/5]">
                                <motion.img
                                    style={{ y, scale: 1.15 }}
                                    src={shownImg}
                                    alt={`${product.name} by Al Zahra`}
                                    className="w-full h-full object-cover"
                                    data-testid={`product-image-${product.id}`}
                                />
                                <span
                                    className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-charcoal/80 via-charcoal/30 to-transparent px-5 pb-4 pt-12 text-[10px] tracking-[0.3em] uppercase text-goldlight"
                                    data-testid={`product-series-${product.id}`}
                                >
                                    {product.series || "The French Series"}
                                </span>
                            </div>
                        </Reveal>
                        {gallery.length > 1 && (
                            <Reveal delay={0.1}>
                                <div className="flex gap-3 mt-4" data-testid={`product-gallery-${product.id}`}>
                                    {gallery.map((img, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setActiveImg(img)}
                                            data-testid={`product-thumb-${product.id}-${i}`}
                                            className={`relative w-20 h-24 overflow-hidden border transition-colors duration-300 ${
                                                activeImg === img || (shownImg === img && !gallery.includes(activeImg)) ? "border-gold" : "border-gold/25 hover:border-gold/60"
                                            }`}
                                        >
                                            <img src={img} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            </Reveal>
                        )}
                        <Reveal delay={0.15}>
                            <div className="flex justify-between mt-4 text-[10px] tracking-[0.25em] uppercase text-ash">
                                <span>No. 0{index + 1}</span>
                                <span className="flex items-center gap-2">
                                    {caption}
                                    {isFrance && <FrenchFlag />}
                                </span>
                            </div>
                        </Reveal>
                    </div>
                </div>

                <div className={reversed ? "lg:order-1 flex flex-col justify-center" : "flex flex-col justify-center"}>
                    <Reveal>
                        <p className="text-xs tracking-[0.3em] uppercase text-golddark mb-6">{product.tag}</p>
                        <h2
                            className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight text-charcoal mb-8"
                            style={product.accent ? { color: product.accent } : undefined}
                            data-testid={`product-name-${product.id}`}
                        >
                            {product.name}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="text-smoke text-base leading-relaxed mb-12 max-w-lg">{description}</p>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <NotesPyramid notes={notes} labels={labels} testId={`notes-pyramid-${product.id}`} />
                    </Reveal>
                    {product.sizes && (
                        <Reveal delay={0.22}>
                            <div className="mt-10" data-testid={`size-selector-${product.id}`}>
                                <span className="text-[10px] tracking-[0.25em] uppercase text-ash block mb-3">{sizeLabel}</span>
                                <div className="flex flex-wrap gap-2">
                                    {product.sizes.map((s, i) => (
                                        <button
                                            key={s.label}
                                            data-testid={`size-${product.id}-${s.label.replace(/\s/g, "")}`}
                                            onClick={() => setSizeIdx(i)}
                                            className={`px-5 py-2.5 text-xs tracking-[0.15em] uppercase border transition-colors duration-300 ${
                                                i === sizeIdx
                                                    ? "bg-charcoal text-goldlight border-charcoal"
                                                    : "border-gold/40 text-smoke hover:border-gold hover:text-golddark"
                                            }`}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </Reveal>
                    )}
                    <Reveal delay={0.25}>
                        <div className="flex items-end justify-between mt-12 mb-8">
                            <div>
                                <span className="text-[10px] tracking-[0.25em] uppercase text-ash block mb-2">{t("priceLabel")}</span>
                                <span className="font-serif text-4xl text-charcoal" data-testid={`product-price-${product.id}`}>
                                    {formatPrice(priceInr, lang, priceAed)}
                                </span>
                                {original && (
                                    <span className="ml-3 font-serif text-xl text-ash line-through" data-testid={`product-original-price-${product.id}`}>
                                        {formatPrice(original, lang)}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <span className="text-[10px] tracking-[0.2em] uppercase text-golddark border border-gold/40 px-3 py-2">
                                    {badge}
                                </span>
                                <span className="text-[10px] tracking-[0.2em] uppercase text-smoke border border-charcoal/20 px-3 py-2" data-testid={`badge-worldwide-${product.id}`}>
                                    {t("badge.worldwide")}
                                </span>
                            </div>
                        </div>
                        <button
                            data-testid={`add-to-cart-${product.id}`}
                            onClick={handleAdd}
                            disabled={!inStock}
                            className="group w-full flex items-center justify-center gap-3 bg-charcoal text-goldlight px-10 py-5 text-xs tracking-[0.25em] uppercase hover:bg-golddark hover:text-ivory transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <Plus size={15} strokeWidth={1.5} className="group-hover:rotate-90 transition-transform duration-300" />
                            {inStock ? t("addToCart") : soldOut}
                        </button>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
